import { appleVendor } from "../../system/platform.ts";
import { loadBuffer } from "./buffer.ts";
import { cache } from "./cache.ts";
import { audioEngine } from "./core.ts";
import { installSpatialOnSound } from "./spatial.ts";
import {
	type EventListener,
	type HTMLAudioElementWithUnlocked,
	isGainNode,
	isHTMLAudioElement,
	type QueueItem,
	type SoundOptions,
} from "./types.ts";
import { Voice } from "./voice.ts";

class Sound {
	/**
	 * @ignore
	 * @internal
	 */
	_autoplay: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_format: string[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_html5: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_muted: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_loop: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_pool: number = 5;
	/**
	 * @ignore
	 * @internal
	 */
	_preload: boolean | "metadata" = true;
	/**
	 * @ignore
	 * @internal
	 */
	_rate: number = 1;
	/**
	 * @ignore
	 * @internal
	 */
	_sprite: Record<string, [number, number, boolean?]> = {};
	/**
	 * @ignore
	 * @internal
	 */
	_src: string | string[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_volume: number = 1;
	/**
	 * @ignore
	 * @internal
	 */
	_xhr: {
		method: string;
		headers?: HeadersInit | undefined;
		withCredentials: boolean;
	} = { method: "GET", withCredentials: false };
	/**
	 * @ignore
	 * @internal
	 */
	_duration: number = 0;
	/**
	 * @ignore
	 * @internal
	 */
	_state: string = "unloaded";
	/**
	 * @ignore
	 * @internal
	 */
	_sounds: Voice[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_endTimers: Record<number, ReturnType<typeof setTimeout>> = {};
	/**
	 * @ignore
	 * @internal
	 */
	_queue: QueueItem[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_playLock: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_webAudio: boolean = false;
	/**
	 * @ignore
	 * @internal
	 */
	_onend: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onfade: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onload: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onloaderror: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onplayerror: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onpause: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onplay: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onstop: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onmute: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onvolume: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onrate: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onseek: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onunlock: EventListener[] = [];
	/**
	 * @ignore
	 * @internal
	 */
	_onresume: EventListener[] = [];

	constructor(o: SoundOptions) {
		if (!o.src || o.src.length === 0) {
			console.error(
				"An array of source files must be passed with any new Sound.",
			);
			return;
		}

		this.init(o);
	}

	init(o: SoundOptions): this {
		audioEngine.ensureContext();

		this._autoplay = o.autoplay || false;
		this._format = typeof o.format !== "string" ? o.format || [] : [o.format];
		this._html5 = o.html5 || false;
		this._muted = o.mute || false;
		this._loop = o.loop || false;
		this._pool = o.pool || 5;
		this._preload =
			typeof o.preload === "boolean" || o.preload === "metadata"
				? o.preload
				: true;
		this._rate = o.rate || 1;
		this._sprite = o.sprite || {};
		this._src = typeof o.src !== "string" ? o.src : [o.src];
		this._volume = o.volume !== undefined ? o.volume : 1;
		this._xhr = {
			method: o.xhr && o.xhr.method ? o.xhr.method : "GET",
			headers: o.xhr && o.xhr.headers ? o.xhr.headers : undefined,
			// Both spellings. `xhr.withCredentials` is the real option; the
			// flat `xhrWithCredentials` is what the pre-20.3 audio backend
			// took, and games still pass it straight through to a `Sound`.
			// Dropping it silently sent authenticated requests without their
			// cookie, which fails as a hung preload rather than a visible
			// error. The nested form wins when both are given.
			withCredentials:
				o.xhr?.withCredentials ??
				// eslint-disable-next-line @typescript-eslint/no-deprecated -- reading the deprecated spelling IS the compatibility
				o.xhrWithCredentials ??
				false,
		};

		this._duration = 0;
		this._state = "unloaded";
		this._sounds = [];
		this._endTimers = {};
		this._queue = [];
		this._playLock = false;

		this._onend = o.onend ? [{ fn: o.onend }] : [];
		this._onfade = o.onfade ? [{ fn: o.onfade }] : [];
		this._onload = o.onload ? [{ fn: o.onload }] : [];
		this._onloaderror = o.onloaderror
			? [
					{
						fn: (...args: unknown[]) => {
							// The id is `null` for a failure that belongs to the CLIP
							// rather than to one playing voice — decode failed, no codec
							// for any source, no audio support at all. Those are exactly
							// the failures a preload has to hear about, and insisting on
							// a numeric voice id swallowed every one of them: the
							// listener never fired, so there was no retry, no give-up
							// and no callback either way, and a preload waiting on the
							// clip simply never settled.
							if (
								o.onloaderror &&
								(args[0] === null || typeof args[0] === "number") &&
								typeof args[1] === "string"
							) {
								o.onloaderror(args[0], args[1]);
							}
						},
					},
				]
			: [];
		this._onplayerror = o.onplayerror
			? [
					{
						fn: (...args: unknown[]) => {
							if (
								o.onplayerror &&
								typeof args[0] === "number" &&
								typeof args[1] === "string"
							) {
								o.onplayerror(args[0], args[1]);
							}
						},
					},
				]
			: [];
		this._onpause = o.onpause ? [{ fn: o.onpause }] : [];
		this._onplay = o.onplay ? [{ fn: o.onplay }] : [];
		this._onstop = o.onstop ? [{ fn: o.onstop }] : [];
		this._onmute = o.onmute ? [{ fn: o.onmute }] : [];
		this._onvolume = o.onvolume ? [{ fn: o.onvolume }] : [];
		this._onrate = o.onrate ? [{ fn: o.onrate }] : [];
		this._onseek = o.onseek ? [{ fn: o.onseek }] : [];
		this._onunlock = o.onunlock ? [{ fn: o.onunlock }] : [];
		this._onresume = [];

		this._webAudio = audioEngine.usingWebAudio && !this._html5;

		if (
			typeof audioEngine.ctx !== "undefined" &&
			audioEngine.ctx &&
			audioEngine.autoUnlock
		) {
			audioEngine._unlockAudio();
		}

		audioEngine.sounds.push(this);

		// Execute plugin hooks
		installSpatialOnSound(this, o);

		if (this._autoplay) {
			this._queue.push({
				event: "play",
				action: () => {
					this.play();
				},
			});
		}

		if (this._preload === true || this._preload === "metadata") {
			this.load();
		}

		return this;
	}

	load(): this {
		let url: string | null = null;

		if (audioEngine.noAudio) {
			this._emit("loaderror", null, "No audio support.");
			return this;
		}

		if (typeof this._src === "string") {
			this._src = [this._src];
		}

		for (let i = 0; i < this._src.length; i++) {
			let ext: string | null;
			const str = this._src[i];

			if (this._format && this._format[i]) {
				ext = this._format[i];
			} else {
				if (typeof str !== "string") {
					this._emit(
						"loaderror",
						null,
						"Non-string found in selected audio sources - ignoring.",
					);
					continue;
				}

				let extMatch = /^data:audio\/([^;,]+);/i.exec(str);
				if (!extMatch) {
					extMatch = /\.([^.]+)$/.exec(str.split("?", 1)[0]);
				}

				ext = extMatch ? extMatch[1].toLowerCase() : null;
			}

			if (!ext) {
				console.warn(
					'No file extension was found. Consider using the "format" property or specify an extension.',
				);
			}

			if (ext && audioEngine.codecs(ext)) {
				url = this._src[i];
				break;
			}
		}

		if (!url) {
			this._emit(
				"loaderror",
				null,
				"No codec support for selected audio sources.",
			);
			return this;
		}

		this._src = url;
		this._state = "loading";

		if (
			globalThis.location?.protocol === "https:" &&
			url.slice(0, 5) === "http:"
		) {
			this._html5 = true;
			this._webAudio = false;
		}

		new Voice(this);

		if (this._webAudio) {
			loadBuffer(this);
		}

		return this;
	}

	play(sprite?: string | number, internal?: boolean): number | null {
		let id: number | null = null;

		if (typeof sprite === "number") {
			id = sprite;
			sprite = undefined;
		} else if (
			typeof sprite === "string" &&
			this._state === "loaded" &&
			!this._sprite[sprite]
		) {
			return null;
		} else if (typeof sprite === "undefined") {
			sprite = "__default";

			if (!this._playLock) {
				let num = 0;
				for (let i = 0; i < this._sounds.length; i++) {
					if (this._sounds[i]._paused && !this._sounds[i]._ended) {
						num++;
						id = this._sounds[i]._id;
					}
				}

				if (num === 1) {
					sprite = undefined;
				} else {
					id = null;
				}
			}
		}

		const sound = id ? this._soundById(id) : this._inactiveSound();

		if (!sound) {
			return null;
		}

		if (id && !sprite) {
			sprite = sound._sprite || "__default";
		}

		if (this._state !== "loaded") {
			sound._sprite = sprite || "__default";
			sound._ended = false;

			const soundId = sound._id;
			this._queue.push({
				event: "play",
				action: () => {
					this.play(soundId);
				},
			});

			return soundId;
		}

		if (id && !sound._paused) {
			if (!internal) {
				this._loadQueue("play");
			}

			return sound._id;
		}

		if (this._webAudio) {
			audioEngine._autoResume();
		}

		const seek = Math.max(
			0,
			sound._seek > 0 ? sound._seek : this._sprite[sprite!][0] / 1000,
		);
		const duration = Math.max(
			0,
			(this._sprite[sprite!][0] + this._sprite[sprite!][1]) / 1000 - seek,
		);
		const timeout = (duration * 1000) / Math.abs(sound._rate);
		const start = this._sprite[sprite!][0] / 1000;
		const stop = (this._sprite[sprite!][0] + this._sprite[sprite!][1]) / 1000;
		sound._sprite = sprite!;

		sound._ended = false;

		const setParams = () => {
			sound._paused = false;
			sound._seek = seek;
			sound._start = start;
			sound._stop = stop;
			sound._loop = !!(sound._loop || this._sprite[sprite!][2]);
		};

		if (seek >= stop) {
			this._ended(sound);
			return sound._id;
		}

		const node = sound._node;

		if (this._webAudio && node && isGainNode(node)) {
			const playWebAudio = () => {
				this._playLock = false;
				setParams();
				this._refreshBuffer(sound);

				const vol = sound._muted || this._muted ? 0 : sound._volume;
				node.gain.setValueAtTime(vol, audioEngine.ctx!.currentTime);
				sound._playStart = audioEngine.ctx!.currentTime;

				if (node.bufferSource) {
					node.bufferSource.start(0, seek, sound._loop ? 86400 : duration);
				}

				if (timeout !== Infinity) {
					this._endTimers[sound._id] = setTimeout(
						this._ended.bind(this, sound),
						timeout,
					);
				}

				if (!internal) {
					setTimeout(() => {
						this._emit("play", sound._id);
						this._loadQueue();
					}, 0);
				}
			};

			if (
				audioEngine.state === "running" &&
				audioEngine.ctx!.state !== "interrupted"
			) {
				playWebAudio();
			} else {
				this._playLock = true;
				this.once("resume", playWebAudio);
				this._clearTimer(sound._id);
			}
		} else if (node && isHTMLAudioElement(node)) {
			const playHtml5 = () => {
				node.currentTime = seek;
				node.muted =
					sound._muted || this._muted || audioEngine._muted || node.muted;
				const volume = audioEngine.volume();
				node.volume = sound._volume * (typeof volume === "number" ? volume : 1);
				node.playbackRate = sound._rate;

				try {
					// typed as always returning a promise, but browsers before the
					// promise-returning spec return undefined
					const play = node.play() as Promise<void> | undefined;

					if (
						play &&
						typeof Promise !== "undefined" &&
						(play instanceof Promise ||
							typeof (play as any).then === "function")
					) {
						this._playLock = true;

						setParams();

						(play as any)
							.then(() => {
								this._playLock = false;
								if ("_unlocked" in node) {
									node._unlocked = true;
								}
								if (!internal) {
									this._emit("play", sound._id);
								} else {
									this._loadQueue();
								}
							})
							.catch(() => {
								this._playLock = false;
								this._emit(
									"playerror",
									sound._id,
									"Playback was unable to start. This is most commonly an issue on mobile devices and Chrome where playback was not within a user interaction.",
								);
								sound._ended = true;
								sound._paused = true;
							});
					} else if (!internal) {
						this._playLock = false;
						setParams();
						this._emit("play", sound._id);
					}

					node.playbackRate = sound._rate;

					if (node.paused) {
						this._emit(
							"playerror",
							sound._id,
							"Playback was unable to start. This is most commonly an issue on mobile devices and Chrome where playback was not within a user interaction.",
						);
						return;
					}

					if (sprite !== "__default" || sound._loop) {
						this._endTimers[sound._id] = setTimeout(
							this._ended.bind(this, sound),
							timeout,
						);
					} else {
						const endHandler = () => {
							this._ended(sound);
							node.removeEventListener("ended", endHandler, false);
						};
						this._endTimers[sound._id] = setTimeout(endHandler, timeout);
						node.addEventListener("ended", endHandler, false);
					}
				} catch (err: unknown) {
					this._emit(
						"playerror",
						sound._id,
						err instanceof Error ? err.message : String(err),
					);
				}
			};

			if (
				node.src ===
				"data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
			) {
				const src =
					typeof this._src === "string"
						? this._src
						: Array.isArray(this._src) && this._src.length > 0
							? this._src[0]
							: "";
				node.src = src;
				node.load();
			}

			const loadedNoReadyState = false;
			if (node.readyState >= 3 || loadedNoReadyState) {
				// deferred a tick: on iOS a play() issued immediately after pause()
				// races the audio node, which pauses again right after starting
				// (goldfire/howler.js#1761)
				setTimeout(playHtml5, 0);
			} else {
				this._playLock = true;
				this._state = "loading";

				const listener = () => {
					this._state = "loaded";
					playHtml5();
					node.removeEventListener(audioEngine._canPlayEvent, listener, false);
				};
				node.addEventListener(audioEngine._canPlayEvent, listener, false);

				this._clearTimer(sound._id);
			}
		}

		return sound._id;
	}

	pause(id?: number, internal?: boolean): this {
		if (this._state !== "loaded" || this._playLock) {
			this._queue.push({
				event: "pause",
				action: () => {
					this.pause(id);
				},
			});

			return this;
		}

		const ids = this._getSoundIds(id);

		for (let i = 0; i < ids.length; i++) {
			this._clearTimer(ids[i]);

			const sound = this._soundById(ids[i]);

			if (sound && !sound._paused) {
				const seekResult = this.seek(ids[i]);
				sound._seek = typeof seekResult === "number" ? seekResult : 0;
				sound._rateSeek = 0;
				sound._paused = true;

				this._stopFade(ids[i]);

				if (sound._node) {
					if (this._webAudio && isGainNode(sound._node)) {
						if (!sound._node.bufferSource) {
							continue;
						}

						sound._node.bufferSource.stop(0);

						this._cleanBuffer(sound._node);
					} else if (
						isHTMLAudioElement(sound._node) &&
						(!isNaN(sound._node.duration) || sound._node.duration === Infinity)
					) {
						sound._node.pause();
					}
				}
			}

			if (!internal) {
				this._emit("pause", sound ? sound._id : null);
			}
		}

		return this;
	}

	stop(id?: number, internal?: boolean): this {
		if (this._state !== "loaded" || this._playLock) {
			this._queue.push({
				event: "stop",
				action: () => {
					// forward `internal`: dropping it made the queued replay emit
					// events the caller had suppressed (goldfire/howler.js#1648)
					this.stop(id, internal);
				},
			});

			return this;
		}

		const ids = this._getSoundIds(id);

		for (let i = 0; i < ids.length; i++) {
			this._clearTimer(ids[i]);

			const sound = this._soundById(ids[i]);

			if (sound) {
				sound._seek = sound._start || 0;
				sound._rateSeek = 0;
				sound._paused = true;
				sound._ended = true;

				this._stopFade(ids[i]);

				if (sound._node) {
					if (this._webAudio && isGainNode(sound._node)) {
						if (sound._node.bufferSource) {
							sound._node.bufferSource.stop(0);

							this._cleanBuffer(sound._node);
						}
					} else if (
						isHTMLAudioElement(sound._node) &&
						(!isNaN(sound._node.duration) || sound._node.duration === Infinity)
					) {
						sound._node.currentTime = sound._start || 0;
						sound._node.pause();

						if (sound._node.duration === Infinity) {
							this._clearSound(sound._node);
						}
					}
				}

				if (!internal) {
					this._emit("stop", sound._id);
				}
			}
		}

		return this;
	}

	mute(muted: boolean, id?: number): boolean | this {
		if (this._state !== "loaded" || this._playLock) {
			this._queue.push({
				event: "mute",
				action: () => {
					this.mute(muted, id);
				},
			});

			return this;
		}

		if (typeof id === "undefined") {
			if (typeof muted === "boolean") {
				this._muted = muted;
			} else {
				return this._muted;
			}
		}

		const ids = this._getSoundIds(id);

		for (let i = 0; i < ids.length; i++) {
			const sound = this._soundById(ids[i]);

			if (sound) {
				sound._muted = muted;

				if (sound._interval) {
					this._stopFade(sound._id);
				}

				if (this._webAudio && sound._node && isGainNode(sound._node)) {
					sound._node.gain.setValueAtTime(
						muted ? 0 : sound._volume,
						audioEngine.ctx!.currentTime,
					);
				} else if (sound._node && isHTMLAudioElement(sound._node)) {
					sound._node.muted = audioEngine._muted ? true : muted;
				}

				this._emit("mute", sound._id);
			}
		}

		return this;
	}

	volume(): number;
	volume(vol: number, id?: number, internal?: boolean): this;
	volume(...args: unknown[]): number | this {
		let volume: number | undefined;
		let id: number | undefined;

		if (args.length === 0) {
			return this._volume;
		} else if (
			args.length === 1 ||
			(args.length === 2 && typeof args[1] === "undefined")
		) {
			const ids = this._getSoundIds();
			const index = ids.indexOf(args[0] as number);
			if (index >= 0) {
				id = parseInt(String(args[0]), 10);
			} else {
				volume = parseFloat(String(args[0]));
			}
		} else if (args.length >= 2) {
			volume = parseFloat(String(args[0]));
			id = parseInt(String(args[1]), 10);
		}

		let sound: Voice | null;
		if (typeof volume !== "undefined" && volume >= 0 && volume <= 1) {
			if (this._state !== "loaded" || this._playLock) {
				this._queue.push({
					event: "volume",
					action: () => {
						if (args.length >= 1 && typeof args[0] === "number") {
							if (args.length >= 2 && typeof args[1] === "number") {
								this.volume(args[0], args[1]);
							} else {
								this.volume(args[0]);
							}
						}
					},
				});

				return this;
			}

			if (typeof id === "undefined") {
				this._volume = volume;
			}

			const soundIds = this._getSoundIds(id);
			for (let i = 0; i < soundIds.length; i++) {
				sound = this._soundById(soundIds[i]);

				if (sound) {
					sound._volume = volume;

					if (!args[2]) {
						this._stopFade(soundIds[i]);
					}

					if (
						this._webAudio &&
						sound._node &&
						isGainNode(sound._node) &&
						!sound._muted
					) {
						sound._node.gain.setValueAtTime(
							volume,
							audioEngine.ctx!.currentTime,
						);
					} else if (
						sound._node &&
						isHTMLAudioElement(sound._node) &&
						!sound._muted
					) {
						const volumeMultiplierOrGlobal = audioEngine.volume();
						if (typeof volumeMultiplierOrGlobal === "number") {
							sound._node.volume = volume * volumeMultiplierOrGlobal;
						}
					}

					this._emit("volume", sound._id);
				}
			}
		} else {
			sound = id ? this._soundById(id) : this._sounds[0];
			return sound ? sound._volume : 0;
		}

		return this;
	}

	fade(from: number, to: number, len: number, id?: number): this {
		if (this._state !== "loaded" || this._playLock) {
			this._queue.push({
				event: "fade",
				action: () => {
					this.fade(from, to, len, id);
				},
			});

			return this;
		}

		from = Math.min(Math.max(0, parseFloat(String(from))), 1);
		to = Math.min(Math.max(0, parseFloat(String(to))), 1);
		len = parseFloat(String(len));

		if (typeof id !== "undefined") {
			this.volume(from, id);
		} else {
			this.volume(from);
		}

		const ids = this._getSoundIds(id);
		for (let i = 0; i < ids.length; i++) {
			const sound = this._soundById(ids[i]);

			if (sound) {
				if (!id) {
					this._stopFade(ids[i]);
				}

				if (this._webAudio && !sound._muted) {
					const currentTime = audioEngine.ctx!.currentTime;
					const end = currentTime + len / 1000;
					sound._volume = from;
					if (sound._node && isGainNode(sound._node)) {
						sound._node.gain.setValueAtTime(from, currentTime);
						sound._node.gain.linearRampToValueAtTime(to, end);
					}
				}

				const isGroup = typeof id === "undefined";

				// A fade with no duration, or one whose endpoints are equal,
				// has nothing to interpolate — and an interval for it is not
				// merely pointless, it is broken twice over. The tick divides
				// the elapsed time by `len`, so a zero duration yields
				// Infinity (or 0/0) and `diff * tick` writes NaN into the
				// volume; and the exit test wants a STRICT inequality between
				// `from` and `to`, which equal endpoints can never satisfy, so
				// the interval would keep ticking forever. Settle on the
				// target immediately instead. `!(len > 0)` rather than
				// `len <= 0` so a NaN duration takes this path too.
				if (!(len > 0) || to === from) {
					if (isGroup) {
						this._volume = to;
					}
					this.volume(to, ids[i]);
					this._emit("fade", ids[i]);
					continue;
				}

				this._startFadeInterval(sound, from, to, len, ids[i], isGroup);
			}
		}

		return this;
	}

	_startFadeInterval(
		sound: Voice,
		from: number,
		to: number,
		len: number,
		_id: number,
		isGroup: boolean,
	): void {
		let vol = from;
		const diff = to - from;
		const steps = Math.abs(diff / 0.01);
		const stepLen = Math.max(4, steps > 0 ? len / steps : len);
		let lastTick = Date.now();

		sound._fadeTo = to;

		sound._interval = setInterval(() => {
			const tick = (Date.now() - lastTick) / len;
			lastTick = Date.now();
			vol += diff * tick;

			vol = Math.round(vol * 100) / 100;

			if (diff < 0) {
				vol = Math.max(to, vol);
			} else {
				vol = Math.min(to, vol);
			}

			if (this._webAudio) {
				sound._volume = vol;
			} else {
				this.volume(vol, sound._id, true);
			}

			if (isGroup) {
				this._volume = vol;
			}

			if ((to < from && vol <= to) || (to > from && vol >= to)) {
				if (sound._interval) {
					clearInterval(sound._interval);
				}
				sound._interval = undefined;
				sound._fadeTo = undefined;
				this.volume(to, sound._id);
				this._emit("fade", sound._id);
			}
		}, stepLen);
	}

	_stopFade(id: number): this {
		const sound = this._soundById(id);

		if (sound && sound._interval) {
			if (this._webAudio && sound._node && isGainNode(sound._node)) {
				sound._node.gain.cancelScheduledValues(audioEngine.ctx!.currentTime);
			}

			if (sound._interval) {
				clearInterval(sound._interval);
				sound._interval = undefined;
			}
			this.volume(sound._fadeTo as number, id);
			sound._fadeTo = undefined;
			this._emit("fade", id);
		}

		return this;
	}

	loop(): boolean;
	loop(loop: boolean, id?: number): this;
	loop(...args: unknown[]): boolean | this {
		let loopVal: boolean | undefined;
		let id: number | undefined;
		let sound: Voice | null;

		if (args.length === 0) {
			return this._loop;
		} else if (args.length === 1) {
			if (typeof args[0] === "boolean") {
				loopVal = args[0];
				this._loop = loopVal;
			} else {
				sound = this._soundById(parseInt(String(args[0]), 10));
				return sound ? sound._loop : false;
			}
		} else if (args.length === 2) {
			loopVal = args[0] as boolean;
			id = parseInt(String(args[1]), 10);
		}

		const ids = this._getSoundIds(id);
		for (let i = 0; i < ids.length; i++) {
			sound = this._soundById(ids[i]);

			if (sound) {
				sound._loop = loopVal as boolean;
				if (
					this._webAudio &&
					sound._node &&
					isGainNode(sound._node) &&
					sound._node.bufferSource
				) {
					sound._node.bufferSource.loop = loopVal ?? false;
					if (loopVal) {
						sound._node.bufferSource.loopStart = sound._start || 0;
						sound._node.bufferSource.loopEnd = sound._stop;

						if (this.playing(ids[i])) {
							this.pause(ids[i], true);
							this.play(ids[i], true);
						}
					}
				}
			}
		}

		return this;
	}

	rate(): number;
	rate(rate: number, id?: number): this;
	rate(...args: unknown[]): number | this {
		let rateVal: number | undefined;
		let id: number | undefined;

		if (args.length === 0) {
			id = this._sounds[0]._id;
		} else if (args.length === 1) {
			const ids = this._getSoundIds();
			const index = ids.indexOf(args[0] as number);
			if (index >= 0) {
				id = parseInt(String(args[0]), 10);
			} else {
				rateVal = parseFloat(String(args[0]));
			}
		} else if (args.length === 2) {
			rateVal = parseFloat(String(args[0]));
			id = parseInt(String(args[1]), 10);
		}

		let sound: Voice | null;
		if (typeof rateVal === "number") {
			if (this._state !== "loaded" || this._playLock) {
				this._queue.push({
					event: "rate",
					action: () => {
						if (args.length >= 1 && typeof args[0] === "number") {
							if (args.length >= 2 && typeof args[1] === "number") {
								this.rate(args[0], args[1]);
							} else {
								this.rate(args[0]);
							}
						}
					},
				});

				return this;
			}

			if (typeof id === "undefined") {
				this._rate = rateVal;
			}

			const soundIds = this._getSoundIds(id);
			for (let i = 0; i < soundIds.length; i++) {
				sound = this._soundById(soundIds[i]);

				if (sound) {
					if (this.playing(soundIds[i])) {
						const seekResult = this.seek(soundIds[i]);
						sound._rateSeek = typeof seekResult === "number" ? seekResult : 0;
						sound._playStart = this._webAudio
							? audioEngine.ctx!.currentTime
							: sound._playStart;
					}
					sound._rate = rateVal;

					if (
						this._webAudio &&
						sound._node &&
						isGainNode(sound._node) &&
						sound._node.bufferSource
					) {
						sound._node.bufferSource.playbackRate.setValueAtTime(
							rateVal,
							audioEngine.ctx!.currentTime,
						);
					} else if (sound._node && isHTMLAudioElement(sound._node)) {
						sound._node.playbackRate = rateVal;
					}

					const seekResult = this.seek(soundIds[i]);
					const seek = typeof seekResult === "number" ? seekResult : 0;
					const duration =
						(this._sprite[sound._sprite][0] + this._sprite[sound._sprite][1]) /
							1000 -
						seek;
					const timeout = (duration * 1000) / Math.abs(sound._rate);

					if (this._endTimers[soundIds[i]] || !sound._paused) {
						this._clearTimer(soundIds[i]);
						this._endTimers[soundIds[i]] = setTimeout(
							this._ended.bind(this, sound),
							timeout,
						);
					}

					this._emit("rate", sound._id);
				}
			}
		} else {
			if (typeof id !== "undefined") {
				sound = this._soundById(id);
				return sound ? sound._rate : this._rate;
			}
			return this._rate;
		}

		return this;
	}

	seek(): number;
	seek(seek: number, id?: number): this;
	seek(...args: unknown[]): number | this {
		let seekVal: number | undefined;
		let id: number | undefined;

		if (args.length === 0) {
			if (this._sounds.length) {
				id = this._sounds[0]._id;
			}
		} else if (args.length === 1) {
			const ids = this._getSoundIds();
			const index = ids.indexOf(args[0] as number);
			if (index >= 0) {
				id = parseInt(String(args[0]), 10);
			} else if (this._sounds.length) {
				id = this._sounds[0]._id;
				seekVal = parseFloat(String(args[0]));
			}
		} else if (args.length === 2) {
			seekVal = parseFloat(String(args[0]));
			id = parseInt(String(args[1]), 10);
		}

		if (typeof id === "undefined") {
			return 0;
		}

		if (
			typeof seekVal === "number" &&
			(this._state !== "loaded" || this._playLock)
		) {
			this._queue.push({
				event: "seek",
				action: () => {
					if (args.length >= 1 && typeof args[0] === "number") {
						if (args.length >= 2 && typeof args[1] === "number") {
							this.seek(args[0], args[1]);
						} else {
							this.seek(args[0]);
						}
					}
				},
			});

			return this;
		}

		const sound = this._soundById(id);

		if (sound) {
			if (typeof seekVal === "number" && seekVal >= 0) {
				const playing = this.playing(id);
				if (playing) {
					this.pause(id, true);
				}

				sound._seek = seekVal;
				sound._ended = false;
				this._clearTimer(id);

				if (
					!this._webAudio &&
					sound._node &&
					isHTMLAudioElement(sound._node) &&
					!isNaN(sound._node.duration)
				) {
					sound._node.currentTime = seekVal;
				}

				const seekAndEmit = () => {
					if (playing) {
						this.play(id, true);
					}

					this._emit("seek", id);
				};

				if (playing && !this._webAudio) {
					const emitSeek = () => {
						if (!this._playLock) {
							seekAndEmit();
						} else {
							setTimeout(emitSeek, 0);
						}
					};
					setTimeout(emitSeek, 0);
				} else {
					seekAndEmit();
				}
			} else {
				if (this._webAudio) {
					const realTime = this.playing(id)
						? audioEngine.ctx!.currentTime - sound._playStart
						: 0;
					const rateSeek = sound._rateSeek ? sound._rateSeek - sound._seek : 0;
					let position =
						sound._seek + (rateSeek + realTime * Math.abs(sound._rate));
					// keep the reported position inside the active loop: the JS end
					// timer can fire after the buffer has already restarted, which
					// otherwise reports a position past the loop end
					// (goldfire/howler.js#1776)
					if (sound._loop && typeof sound._stop === "number") {
						const loopStart = sound._start || 0;
						const loopDuration = sound._stop - loopStart;
						if (loopDuration > 0 && position >= sound._stop) {
							position = loopStart + ((position - loopStart) % loopDuration);
						}
					}
					return position;
				} else if (sound._node && isHTMLAudioElement(sound._node)) {
					return sound._node.currentTime;
				}
				return 0;
			}
		}

		return this;
	}

	playing(id?: number): boolean {
		if (typeof id === "number") {
			const sound = this._soundById(id);
			return sound ? !sound._paused : false;
		}

		for (let i = 0; i < this._sounds.length; i++) {
			if (!this._sounds[i]._paused) {
				return true;
			}
		}

		return false;
	}

	duration(id?: number): number {
		let duration = this._duration;

		if (typeof id !== "undefined") {
			const sound = this._soundById(id);
			if (sound) {
				duration = this._sprite[sound._sprite][1] / 1000;
			}
		}

		return duration;
	}

	state(): string {
		return this._state;
	}

	unload(): null {
		// Execute plugin hooks before destruction

		const sounds = this._sounds;
		for (let i = 0; i < sounds.length; i++) {
			if (!sounds[i]._paused) {
				this.stop(sounds[i]._id);
			}

			const node = sounds[i]._node;
			if (!this._webAudio && node && isHTMLAudioElement(node)) {
				this._clearSound(node);

				const errorFn = sounds[i]._errorFn;
				if (errorFn) {
					node.removeEventListener("error", errorFn, false);
				}
				const loadFn = sounds[i]._loadFn;
				if (loadFn) {
					node.removeEventListener(audioEngine._canPlayEvent, loadFn, false);
				}
				const endFn = sounds[i]._endFn;
				if (endFn) {
					node.removeEventListener("ended", endFn, false);
				}

				audioEngine._releaseHtml5Audio(node);
			}

			sounds[i]._node = null;

			this._clearTimer(sounds[i]._id);
		}

		const index = audioEngine.sounds.indexOf(this);
		if (index >= 0) {
			audioEngine.sounds.splice(index, 1);
		}

		let remCache = true;
		for (let i = 0; i < audioEngine.sounds.length; i++) {
			if (
				audioEngine.sounds[i]._src === this._src ||
				(this._src as string).indexOf(audioEngine.sounds[i]._src as string) >= 0
			) {
				remCache = false;
				break;
			}
		}

		if (cache && remCache) {
			delete cache[this._src as string];
		}

		audioEngine.noAudio = false;

		this._state = "unloaded";
		this._sounds = [];

		return null;
	}

	on(
		event: string,
		fn: (...args: unknown[]) => void,
		id?: number,
		once?: boolean,
	): this {
		const events = (this as unknown as Record<string, EventListener[]>)[
			`_on${event}`
		];

		if (typeof fn === "function") {
			events.push(once ? { id, fn, once } : { id, fn });
		}

		return this;
	}

	off(event: string, fn?: (...args: unknown[]) => void, id?: number): this {
		const events = (this as unknown as Record<string, EventListener[]>)[
			`_on${event}`
		];
		let i: number;

		if (typeof fn === "number") {
			id = fn;
			fn = undefined;
		}

		if (fn || id) {
			for (i = 0; i < events.length; i++) {
				const isId = id === events[i].id;
				if ((fn === events[i].fn && isId) || (!fn && isId)) {
					events.splice(i, 1);
					break;
				}
			}
		} else if (event) {
			(this as unknown as Record<string, EventListener[]>)[`_on${event}`] = [];
		} else {
			const keys = Object.keys(this);
			for (i = 0; i < keys.length; i++) {
				if (
					keys[i].indexOf("_on") === 0 &&
					Array.isArray(
						(this as unknown as Record<string, EventListener[]>)[keys[i]],
					)
				) {
					(this as unknown as Record<string, EventListener[]>)[keys[i]] = [];
				}
			}
		}

		return this;
	}

	once(event: string, fn: (...args: unknown[]) => void, id?: number): this {
		this.on(event, fn, id, true);

		return this;
	}

	_emit(event: string, id?: number | null, msg?: string): this {
		const events = (this as unknown as Record<string, EventListener[]>)[
			`_on${event}`
		];

		for (let i = events.length - 1; i >= 0; i--) {
			if (!events[i].id || events[i].id === id || event === "load") {
				const fn = events[i].fn;
				setTimeout(() => {
					fn(id, msg);
				}, 0);

				if (events[i].once) {
					this.off(event, events[i].fn, events[i].id);
				}
			}
		}

		this._loadQueue(event);

		return this;
	}

	_loadQueue(event?: string): this {
		if (this._queue.length > 0) {
			const task = this._queue[0];

			if (task.event === event) {
				this._queue.shift();
				this._loadQueue();
			}

			if (!event) {
				task.action();
			}
		}

		return this;
	}

	_ended(sound: Voice): this {
		const sprite = sound._sprite;

		if (
			!this._webAudio &&
			sound._node &&
			isHTMLAudioElement(sound._node) &&
			!sound._node.paused &&
			!sound._node.ended &&
			sound._node.currentTime < sound._stop!
		) {
			setTimeout(this._ended.bind(this, sound), 100);
			return this;
		}

		const loop = !!(sound._loop || this._sprite[sprite][2]);

		this._emit("end", sound._id);

		if (!this._webAudio && loop) {
			this.stop(sound._id, true).play(sound._id);
		}

		if (this._webAudio && loop) {
			this._emit("play", sound._id);
			sound._seek = sound._start || 0;
			sound._rateSeek = 0;
			sound._playStart = audioEngine.ctx!.currentTime;

			const timeout =
				((sound._stop! - (sound._start || 0)) * 1000) / Math.abs(sound._rate);
			this._endTimers[sound._id] = setTimeout(
				this._ended.bind(this, sound),
				timeout,
			);
		}

		if (this._webAudio && !loop) {
			sound._paused = true;
			sound._ended = true;
			sound._seek = sound._start || 0;
			sound._rateSeek = 0;
			this._clearTimer(sound._id);

			this._cleanBuffer(sound._node);

			audioEngine._autoSuspend();
		}

		if (!this._webAudio && !loop) {
			this.stop(sound._id, true);
		}

		return this;
	}

	_clearTimer(id: number): this {
		if (this._endTimers[id]) {
			if (typeof this._endTimers[id] !== "function") {
				clearTimeout(this._endTimers[id]);
			} else {
				const sound = this._soundById(id);
				if (sound && sound._node) {
					sound._node.removeEventListener("ended", this._endTimers[id], false);
				}
			}

			delete this._endTimers[id];
		}

		return this;
	}

	_soundById(id: number): Voice | null {
		for (let i = 0; i < this._sounds.length; i++) {
			if (id === this._sounds[i]._id) {
				return this._sounds[i];
			}
		}

		return null;
	}

	_inactiveSound(): Voice {
		this._drain();

		for (let i = 0; i < this._sounds.length; i++) {
			if (this._sounds[i]._ended) {
				return this._sounds[i].reset();
			}
		}

		return new Voice(this);
	}

	_drain(): void {
		const limit = this._pool;
		let cnt = 0;

		if (this._sounds.length < limit) {
			return;
		}

		for (let i = 0; i < this._sounds.length; i++) {
			if (this._sounds[i]._ended) {
				cnt++;
			}
		}

		for (let i = this._sounds.length - 1; i >= 0; i--) {
			if (cnt <= limit) {
				return;
			}

			if (this._sounds[i]._ended) {
				const node = this._sounds[i]._node;
				if (this._webAudio && node && isGainNode(node)) {
					node.disconnect(0);
				}

				this._sounds.splice(i, 1);
				cnt--;
			}
		}
	}

	_getSoundIds(id?: number): number[] {
		if (typeof id === "undefined") {
			const ids: number[] = [];
			for (let i = 0; i < this._sounds.length; i++) {
				ids.push(this._sounds[i]._id);
			}

			return ids;
		} else {
			return [id];
		}
	}

	_refreshBuffer(sound: Voice): this {
		if (!sound._node || !isGainNode(sound._node) || !audioEngine.ctx) {
			return this;
		}

		sound._node.bufferSource = audioEngine.ctx.createBufferSource();
		const src =
			typeof this._src === "string"
				? this._src
				: Array.isArray(this._src) && this._src.length > 0
					? this._src[0]
					: "";
		sound._node.bufferSource.buffer = cache[src];

		if (sound._panner) {
			sound._node.bufferSource.connect(sound._panner);
		} else {
			sound._node.bufferSource.connect(sound._node);
		}

		sound._node.bufferSource.loop = sound._loop;
		if (sound._loop) {
			sound._node.bufferSource.loopStart = sound._start || 0;
			sound._node.bufferSource.loopEnd = sound._stop || 0;
		}
		sound._node.bufferSource.playbackRate.setValueAtTime(
			sound._rate,
			audioEngine.ctx.currentTime,
		);

		return this;
	}

	_cleanBuffer(node: any): this {
		// WebKit needs the buffer detached through a scratch buffer
		const isWebKit = appleVendor;

		if (!node.bufferSource) {
			return this;
		}

		if (audioEngine._scratchBuffer && node.bufferSource) {
			node.bufferSource.onended = null;
			node.bufferSource.disconnect(0);
			if (isWebKit) {
				try {
					node.bufferSource.buffer = audioEngine._scratchBuffer;
				} catch {
					// the node may already be disconnected; nothing to undo
				}
			}
		}
		node.bufferSource = null;

		return this;
	}

	_clearSound(node: HTMLAudioElementWithUnlocked): void {
		node.src =
			"data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
	}
}

export { Sound };
